// telas/Pedido.tsx - O pedido da dinâmica (07_VOZ §5; mockups da Dinâmica; D62).
//
// Duas etapas numa tela: "Sobre a turma" (idade, matéria, objetivo, lugar) e "Quem está na sua turma?"
// (inclusão, o DUA) com "Algo mais?". Os campos e as opções são os da Dinâmica de hoje (F10, D25); muda só a
// apresentação. Um botão laranja: "Criar dinâmica".

import type { FormEvent, ReactNode } from 'react';
import { Button, Chip, ChipGroup, IconSparkles, SelectField, Step, TextAreaField } from '@winieduca/ui';
import type { OpcoesDisponiveis } from '../services/api';
import { acoes, ajudaObjetivo, pedido, pedidoNovos, semEscolha } from '../content/textos';

export const LIMITE_ALGO_MAIS = 200;

export interface ValoresDoPedido {
  faixaEtaria: number;
  materia: string;
  objetivo: string;
  espaco: string;
  inclusao: string[];
  refinamento: string;
}

export type ErrosDoPedido = Partial<Record<'materia' | 'objetivo' | 'espaco' | 'refinamento', string>>;

export function validarPedido(v: ValoresDoPedido): ErrosDoPedido {
  const erros: ErrosDoPedido = {};
  if (!v.materia) erros.materia = pedidoNovos.erros.materia;
  if (!v.objetivo) erros.objetivo = pedidoNovos.erros.objetivo;
  if (!v.espaco) erros.espaco = pedidoNovos.erros.onde;
  if (v.refinamento.length > LIMITE_ALGO_MAIS) erros.refinamento = pedidoNovos.erros.algoMais;
  return erros;
}

export function Pedido({
  opcoes,
  valores,
  mudar,
  erros,
  enviando,
  aviso,
  onCriar,
}: {
  opcoes: OpcoesDisponiveis;
  valores: ValoresDoPedido;
  mudar: (parcial: Partial<ValoresDoPedido>) => void;
  erros: ErrosDoPedido;
  enviando: boolean;
  /** Limite do dia ou erro da última tentativa, logo abaixo do título: é onde a tela abre e volta depois de uma falha. */
  aviso?: ReactNode;
  onCriar: () => void;
}) {
  function enviar(e: FormEvent) {
    e.preventDefault();
    onCriar();
  }

  return (
    <form className="din-pedido" noValidate onSubmit={enviar} aria-labelledby="pedido-titulo">
      <header className="din-abertura">
        <h1 id="pedido-titulo" className="we-display din-titulo" tabIndex={-1}>
          {pedido.titulo}
        </h1>
        <p className="we-lead din-subtitulo">{pedido.subtitulo}</p>
      </header>

      {aviso}

      <Step number={1} title={pedido.sobre}>
        <div className="din-duas">
          <SelectField label={pedido.idade} name="faixaEtaria" value={String(valores.faixaEtaria)} onChange={(e) => mudar({ faixaEtaria: Number(e.target.value) })}>
            {opcoes.faixasEtarias.map((idade) => (
              <option key={idade} value={idade}>
                {pedidoNovos.anos(idade)}
              </option>
            ))}
          </SelectField>
          <SelectField label={pedido.materia} name="materia" value={valores.materia} error={erros.materia} onChange={(e) => mudar({ materia: e.target.value })}>
            <option value="">{semEscolha.materia}</option>
            {opcoes.materias.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </SelectField>
        </div>
        <SelectField label={pedido.objetivo} name="objetivo" value={valores.objetivo} error={erros.objetivo} onChange={(e) => mudar({ objetivo: e.target.value })}>
          <option value="">{ajudaObjetivo.texto}</option>
          {opcoes.objetivos.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </SelectField>
        <SelectField label={pedido.onde} name="espaco" value={valores.espaco} error={erros.espaco} onChange={(e) => mudar({ espaco: e.target.value })}>
          <option value="">{pedidoNovos.ondePlaceholder}</option>
          {opcoes.espacos.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </SelectField>
      </Step>

      <Step number={2} title={pedido.quem}>
        {/* A pergunta já é o título da etapa: a legenda fica só para o leitor de tela. */}
        <div className="din-quem">
          <ChipGroup legend={pedido.quem} help={pedido.dua}>
            {opcoes.necessidadesInclusao.map((n) => (
              <Chip
                key={n}
                pressed={valores.inclusao.includes(n)}
                onPressedChange={(on) => mudar({ inclusao: on ? [...valores.inclusao, n] : valores.inclusao.filter((x) => x !== n) })}
              >
                {n}
              </Chip>
            ))}
          </ChipGroup>
        </div>
        <TextAreaField
          label={pedido.algoMais}
          name="refinamento"
          rows={3}
          value={valores.refinamento}
          placeholder={pedidoNovos.algoMaisExemplo}
          help={pedidoNovos.contador(valores.refinamento.length, LIMITE_ALGO_MAIS)}
          error={erros.refinamento}
          onChange={(e) => mudar({ refinamento: e.target.value })}
        />
      </Step>

      {/* No celular, o botão fica preso embaixo, acima da barra de seções (mockup do pedido). */}
      <div className="din-barra">
        <Button type="submit" variant="primary" icon={IconSparkles} block loading={enviando} className="din-criar">
          {acoes.criar}
        </Button>
      </div>
    </form>
  );
}
